import type {
  AxisSpec,
  CampaignConfig,
  OutputSpec,
  PostDivergenceSamplingPolicy,
  SamplePoint,
  SamplerConfig,
  SamplerDecision,
  TailorExpertName,
  TailorExpertScore,
} from '../types';
import { predict, predictDifference } from './surrogate';
import {
  fitAllOutputs,
  fixedValues,
  inferDivergenceFrontier,
  initialDesign,
  applySymmetrySamplingInputs,
  isAllowedByDivergenceFrontier,
  isAllowedBySymmetrySampling,
  makeRng,
  normalisePoint,
  rangeAxes,
  symmetryAugmentedObservations,
  type PerOutputModel,
} from './_mock_sampler';

export type MannequinInstance = {
  id: string;
  label: string;
  liftSlope: number;
  stallAlpha: number;
  clMax: number;
  betaCoupling: number;
  dragBase: number;
  pitchBias: number;
};

type TailorProposal = {
  inputs: Record<string, number>;
  decision: SamplerDecision;
};

const EXPERTS: TailorExpertName[] = [
  'value_uncertainty',
  'gradient',
  'topology',
  'downstream',
  'coverage',
];

const DEFAULT_WEIGHTS: Record<TailorExpertName, number> = {
  value_uncertainty: 0.26,
  gradient: 0.24,
  topology: 0.18,
  downstream: 0.12,
  coverage: 0.2,
};

const OUTPUT_ALIASES: Record<string, string> = {
  C_L: 'CL',
  cl: 'CL',
  C_D: 'CD',
  cd: 'CD',
  C_Y: 'CY',
  cy: 'CY',
  Cl: 'Cmx',
  C_l: 'Cmx',
  cmx: 'Cmx',
  C_m: 'Cmy',
  Cm: 'Cmy',
  cm: 'Cmy',
  cmy: 'Cmy',
  Cn: 'Cmz',
  C_n: 'Cmz',
  cn: 'Cmz',
  cmz: 'Cmz',
};

export function canonicalTailorOutputName(output: string): string {
  return OUTPUT_ALIASES[output] ?? output;
}

function outputAliases(output: string): string[] {
  const canonical = canonicalTailorOutputName(output);
  const aliases = new Set([output, canonical, canonical.toLowerCase()]);
  if (canonical === 'CL') aliases.add('C_L');
  if (canonical === 'CD') aliases.add('C_D');
  if (canonical === 'CY') aliases.add('C_Y');
  if (canonical === 'Cmx') {
    aliases.add('Cl');
    aliases.add('C_l');
    aliases.add('cmx');
  }
  if (canonical === 'Cmy') {
    aliases.add('C_m');
    aliases.add('Cm');
    aliases.add('cm');
    aliases.add('cmy');
  }
  if (canonical === 'Cmz') {
    aliases.add('Cn');
    aliases.add('C_n');
    aliases.add('cn');
    aliases.add('cmz');
  }
  return [...aliases];
}

function outputValue(source: Record<string, number> | undefined, output: string): number | null {
  if (!source) return null;
  for (const alias of outputAliases(output)) {
    const value = source[alias];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

type MannequinFamilyProfile = {
  label: string;
  liftSlope: number;
  stallAlpha: number;
  stallSpread: number;
  clMax: number;
  clMaxSpread: number;
  betaCoupling: number;
  dragBase: number;
  pitchBias: number;
};

function normaliseFamilyName(family: string): string {
  return family.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
}

function familyLabel(family: string): string {
  return family
    .replace(/^synthetic[-_\s]+/i, '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => {
      if (/^x57$/i.test(part)) return 'X-57';
      if (/^evtol$/i.test(part)) return 'eVTOL';
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

function familyProfile(family: string): MannequinFamilyProfile {
  const normalised = normaliseFamilyName(family);
  if (normalised.includes('x57') || normalised.includes('x-57')) {
    return {
      label: 'X-57-like Mannequin',
      liftSlope: 0.14,
      stallAlpha: 14.5,
      stallSpread: 3.1,
      clMax: 1.62,
      clMaxSpread: 0.42,
      betaCoupling: 0.005,
      dragBase: 0.038,
      pitchBias: -0.012,
    };
  }
  if (normalised.includes('evtol')) {
    return {
      label: 'eVTOL-like Mannequin',
      liftSlope: 0.095,
      stallAlpha: 12.4,
      stallSpread: 2.8,
      clMax: 1.32,
      clMaxSpread: 0.5,
      betaCoupling: 0.008,
      dragBase: 0.055,
      pitchBias: -0.018,
    };
  }
  if (normalised.includes('fighter')) {
    return {
      label: 'Fighter-like Mannequin',
      liftSlope: 0.125,
      stallAlpha: 16.2,
      stallSpread: 3.4,
      clMax: 1.72,
      clMaxSpread: 0.46,
      betaCoupling: 0.006,
      dragBase: 0.034,
      pitchBias: -0.008,
    };
  }
  if (normalised.includes('cessna')) {
    return {
      label: 'Cessna-like Mannequin',
      liftSlope: 0.11,
      stallAlpha: 13.5,
      stallSpread: 3.5,
      clMax: 1.45,
      clMaxSpread: 0.45,
      betaCoupling: 0.004,
      dragBase: 0.028,
      pitchBias: -0.025,
    };
  }
  const seed = hashSeed(family);
  return {
    label: `${familyLabel(family)} Mannequin`,
    liftSlope: 0.092 + (seed % 52) / 1000,
    stallAlpha: 12.2 + ((seed >>> 6) % 54) / 10,
    stallSpread: 2.2 + ((seed >>> 11) % 18) / 10,
    clMax: 1.28 + ((seed >>> 15) % 62) / 100,
    clMaxSpread: 0.28 + ((seed >>> 20) % 28) / 100,
    betaCoupling: 0.0035 + ((seed >>> 24) % 48) / 10000,
    dragBase: 0.027 + ((seed >>> 9) % 30) / 1000,
    pitchBias: -0.026 + ((seed >>> 17) % 45) / 1000,
  };
}

function normaliseWeights(
  overrides: SamplerConfig['tailorWeights'] | undefined,
): Record<TailorExpertName, number> {
  const merged = { ...DEFAULT_WEIGHTS, ...(overrides ?? {}) };
  const sum = EXPERTS.reduce((acc, name) => acc + Math.max(0, merged[name] ?? 0), 0) || 1;
  return Object.fromEntries(
    EXPERTS.map((name) => [name, Math.max(0, merged[name] ?? 0) / sum]),
  ) as Record<TailorExpertName, number>;
}

export function createMannequinInstance(
  campaignId: string,
  config: CampaignConfig,
): MannequinInstance {
  const family = config.sampler?.mannequinFamily ?? 'cessna_like';
  const seed = Number.isFinite(config.sampler?.mannequinSeed)
    ? Math.max(0, Math.trunc(config.sampler?.mannequinSeed ?? 0))
    : hashSeed(campaignId);
  const rng = makeRng(`mannequin-seed-${seed}`, `mannequin-${family}`);
  const profile = familyProfile(family);
  return {
    id: `mq_${hashSeed(`${family}:${seed}`).toString(36).slice(0, 7)}`,
    label: profile.label,
    liftSlope: profile.liftSlope * (0.9 + 0.2 * rng()),
    stallAlpha: profile.stallAlpha + profile.stallSpread * rng(),
    clMax: profile.clMax + profile.clMaxSpread * rng(),
    betaCoupling: profile.betaCoupling * (0.75 + 0.5 * rng()),
    dragBase: profile.dragBase * (0.8 + 0.4 * rng()),
    pitchBias: profile.pitchBias - 0.02 + 0.04 * rng(),
  };
}

export function evaluateMannequinPoint(
  point: Record<string, number>,
  outputs: OutputSpec[],
  instance: MannequinInstance,
): Record<string, number> {
  const alpha = point.alpha ?? 0;
  const beta = point.beta ?? 0;
  const aileron = point.aileron ?? point.da ?? 0;
  const elevator = point.elevator ?? point.de ?? 0;
  const rudder = point.rudder ?? point.dr ?? 0;
  const pRate = point.P ?? 0;
  const qRate = point.Q ?? 0;
  const rRate = point.R ?? 0;
  const alphaDot = point.alpha_dot ?? 0;
  const rpm = point.RPM ?? 3500;
  const mach = point.Mach ?? 0.2;
  const re = point.Re ?? 1;
  const blowing = Math.max(0, Math.min(1, point.blowing ?? 0));
  const throttle = Math.max(0, Math.min(1, point.throttle ?? 0));
  const rpmBoost = 1 + Math.max(0, Math.min(1, rpm / 8000)) * 0.12 + throttle * 0.18 + blowing * 0.32;
  const machFactor = 1 - 0.16 * Math.max(0, mach - 0.2) + 0.035 * Math.max(0, 0.3 - mach);
  const reFactor = 1 + 0.035 * Math.tanh((re - 1) / 2);
  const alphaEffective = alpha + 0.32 * elevator + 0.018 * qRate + 0.028 * alphaDot;
  const betaEffective = beta + 0.22 * rudder + 0.018 * rRate + 0.012 * pRate;
  const linearCl = instance.liftSlope * (alphaEffective + 2.2) * rpmBoost * machFactor * reFactor;
  const stallExcess = Math.max(0, alpha - instance.stallAlpha);
  const stallSoftening = 1 - 0.28 * blowing - 0.12 * throttle;
  const stallDrop = 0.055 * Math.max(0, stallExcess * stallSoftening) ** 1.35;
  const cl = Math.min(linearCl, instance.clMax * rpmBoost * machFactor * reFactor)
    - stallDrop
    - instance.betaCoupling * betaEffective ** 2
    + 0.012 * aileron * Math.sin((beta * Math.PI) / 24);
  const cd = instance.dragBase
    + 0.018 * cl ** 2
    + 0.0016 * Math.max(0, alpha) ** 1.45
    + 0.0008 * betaEffective ** 2
    + 0.017 * (mach - 0.2) ** 2
    + 0.00012 * (elevator ** 2 + aileron ** 2 + rudder ** 2)
    + 0.000012 * (pRate ** 2 + qRate ** 2 + rRate ** 2);
  const cy = -0.018 * betaEffective
    + 0.0008 * betaEffective * Math.max(0, alpha - 8)
    + 0.0022 * rudder
    + 0.0009 * aileron
    + 0.0008 * rRate;
  const cmx = 0.012 * betaEffective
    + 0.0037 * Math.sin((alpha * Math.PI) / 12)
    + 0.0017 * aileron
    + 0.00055 * pRate
    + 0.0007 * elevator * beta;
  const cmy = instance.pitchBias
    - 0.012 * alpha
    - 0.045 * Math.tanh(stallExcess / 4)
    - 0.018 * elevator
    - 0.00055 * qRate
    + 0.025 * (mach - 0.2)
    + 0.012 * blowing
    - 0.006 * throttle;
  const cmz = -0.015 * betaEffective
    + 0.002 * alpha * Math.sign(betaEffective || 1)
    + 0.0018 * rudder
    + 0.00045 * rRate
    + 0.0006 * elevator * Math.sign(betaEffective || 1);
  const clHysteresis = Math.max(0, 0.012 * stallExcess + 0.0018 * Math.abs(alphaDot) + 0.018 * blowing);
  const values: Record<string, number> = {
    CL: cl,
    CD: cd,
    CY: cy,
    Cmx: cmx,
    Cmy: cmy,
    Cmz: cmz,
    Cl: cmx,
    Cm: cmy,
    Cn: cmz,
    CL_hysteresis: clHysteresis,
  };
  return Object.fromEntries(outputs.map((o) => [o.name, values[canonicalTailorOutputName(o.name)] ?? 0]));
}

export function evaluateLowFiMannequinPoint(
  point: Record<string, number>,
  outputs: OutputSpec[],
  instance: MannequinInstance,
): Record<string, number> {
  const alpha = point.alpha ?? 0;
  const beta = point.beta ?? 0;
  const aileron = point.aileron ?? point.da ?? 0;
  const elevator = point.elevator ?? point.de ?? 0;
  const rudder = point.rudder ?? point.dr ?? 0;
  const pRate = point.P ?? 0;
  const qRate = point.Q ?? 0;
  const rRate = point.R ?? 0;
  const alphaDot = point.alpha_dot ?? 0;
  const rpm = point.RPM ?? 3500;
  const mach = point.Mach ?? 0.2;
  const blowing = Math.max(0, Math.min(1, point.blowing ?? 0));
  const throttle = Math.max(0, Math.min(1, point.throttle ?? 0));
  const rpmTerm = (rpm - 3500) / 4500;
  const machTerm = mach - 0.2;
  const cl = instance.liftSlope * 0.82 * (alpha + 2.2)
    + 0.032 * rpmTerm
    + 0.026 * elevator
    + 0.006 * qRate
    + 0.010 * alphaDot
    + 0.075 * throttle
    + 0.12 * blowing
    - 0.05 * machTerm;
  const cd = instance.dragBase * 1.18
    + 0.0045 * alpha
    + 0.0012 * beta
    + 0.006 * rpmTerm
    + 0.018 * machTerm
    + 0.0008 * (elevator + aileron + rudder)
    + 0.008 * throttle;
  const cy = -0.015 * beta + 0.0005 * alpha + 0.0015 * rudder + 0.0006 * aileron + 0.0003 * rRate;
  const cmx = 0.010 * beta + 0.0008 * alpha + 0.0014 * aileron + 0.00025 * pRate;
  const cmy = instance.pitchBias + 0.01 - 0.0105 * alpha + 0.0005 * beta - 0.015 * elevator - 0.00025 * qRate + 0.02 * machTerm + 0.006 * blowing;
  const cmz = -0.013 * beta + 0.0007 * alpha + 0.0012 * rudder + 0.0002 * rRate;
  const clHysteresis = Math.max(0, 0.0015 * Math.max(0, alpha - instance.stallAlpha) + 0.0009 * Math.abs(alphaDot) + 0.01 * blowing);
  const values: Record<string, number> = {
    CL: cl,
    CD: cd,
    CY: cy,
    Cmx: cmx,
    Cmy: cmy,
    Cmz: cmz,
    Cl: cmx,
    Cm: cmy,
    Cn: cmz,
    CL_hysteresis: clHysteresis,
  };
  return Object.fromEntries(outputs.map((o) => [o.name, values[canonicalTailorOutputName(o.name)] ?? 0]));
}

function fillFixed(point: Record<string, number>, inputs: AxisSpec[]): Record<string, number> {
  return { ...fixedValues(inputs), ...point };
}

function candidateGrid(range: AxisSpec[], rng: () => number, count = 600): Array<Record<string, number>> {
  const out: Array<Record<string, number>> = [];
  for (let i = 0; i < count; i++) {
    const point: Record<string, number> = {};
    for (const axis of range) {
      const lo = axis.min ?? 0;
      const hi = axis.max ?? 1;
      point[axis.name] = lo + rng() * (hi - lo);
    }
    out.push(point);
  }
  return out;
}

function normaliseScores(values: number[]): number[] {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  if (!Number.isFinite(span) || span <= 1e-9) return values.map(() => 0);
  return values.map((v) => (v - min) / span);
}

function perturb(
  point: Record<string, number>,
  axis: AxisSpec,
  delta: number,
): Record<string, number> {
  const lo = axis.min ?? 0;
  const hi = axis.max ?? 1;
  return {
    ...point,
    [axis.name]: Math.max(lo, Math.min(hi, (point[axis.name] ?? 0) + delta)),
  };
}

function outputVector(
  perOutput: PerOutputModel[],
  point: Record<string, number>,
  range: AxisSpec[],
  outputs?: OutputSpec[],
  instance?: MannequinInstance,
): Record<string, { mean: number; sigma: number }> {
  const x = normalisePoint(point, range);
  const prior = outputs && instance ? evaluateLowFiMannequinPoint(point, outputs, instance) : {};
  return Object.fromEntries(
    perOutput.map(({ name, model }) => {
      const residual = predict(model, x);
      return [name, { mean: (prior[name] ?? 0) + residual.mean, sigma: residual.sigma }];
    }),
  );
}

function residualObservations(
  config: CampaignConfig,
  observed: SamplePoint[],
  instance: MannequinInstance,
): SamplePoint[] {
  const symmetricObserved = symmetryAugmentedObservations(
    config.inputs,
    config.outputs,
    observed,
    config.sampler?.symmetries ?? config.sampler?.symmetry,
  );
  return symmetricObserved.flatMap((point) => {
    const residuals: Record<string, number> = {};
    const prior = evaluateLowFiMannequinPoint(point.inputs, config.outputs, instance);
    for (const output of config.outputs) {
      const measured = outputValue(point.outputs, output.name) ?? outputValue(point.truthOutputs, output.name);
      if (measured == null) continue;
      if (canonicalTailorOutputName(output.name) !== output.name && measured === 0) continue;
      const priorValue = prior[output.name] ?? 0;
      residuals[output.name] = measured - priorValue;
    }
    return Object.keys(residuals).length > 0 ? [{ ...point, outputs: residuals }] : [];
  });
}

export function fitTailorResidualOutputs(
  config: CampaignConfig,
  observed: SamplePoint[],
  instance: MannequinInstance,
): PerOutputModel[] {
  return fitAllOutputs(config.inputs, config.outputs, residualObservations(config, observed, instance));
}

export function predictTailorMannequinOutputs(
  config: CampaignConfig,
  perOutput: PerOutputModel[],
  point: Record<string, number>,
  instance: MannequinInstance,
): Record<string, { mean: number; sigma: number }> {
  return outputVector(perOutput, point, rangeAxes(config.inputs), config.outputs, instance);
}

export type TailorTransportPrediction = {
  /** Mean correction added to the measured value: f(target) - f(source). */
  deltaMean: number;
  /** Joint-posterior standard deviation of that correction. */
  sigma: number;
  covariance: number;
  correlation: number;
};

/**
 * Transport a condition through the current residual GP without discarding the
 * correlation between the source and target predictions. The low-fi prior is
 * deterministic, so all transport variance comes from the residual GP.
 */
export function predictTailorMannequinTransport(
  config: CampaignConfig,
  perOutput: PerOutputModel[],
  source: Record<string, number>,
  target: Record<string, number>,
  instance: MannequinInstance,
): Record<string, TailorTransportPrediction> {
  const range = rangeAxes(config.inputs);
  const sourceX = normalisePoint(source, range);
  const targetX = normalisePoint(target, range);
  const sourcePrior = evaluateLowFiMannequinPoint(source, config.outputs, instance);
  const targetPrior = evaluateLowFiMannequinPoint(target, config.outputs, instance);
  return Object.fromEntries(perOutput.map(({ name, model }) => {
    const residual = predictDifference(model, targetX, sourceX);
    return [name, {
      deltaMean: (targetPrior[name] ?? 0) - (sourcePrior[name] ?? 0) + residual.mean,
      sigma: residual.sigma,
      covariance: residual.covariance,
      correlation: residual.correlation,
    }];
  }));
}

function computeMaxAbsTailorMu(
  inputs: AxisSpec[],
  range: AxisSpec[],
  perOutput: PerOutputModel[],
  rng: () => number,
  outputs: OutputSpec[],
  instance: MannequinInstance,
): Record<string, number> {
  const out: Record<string, number> = {};
  const samples = candidateGrid(range, rng, 50).map((point) => fillFixed(point, inputs));
  for (const { name } of perOutput) {
    let m = 1e-6;
    for (const sample of samples) {
      const pred = outputVector(perOutput, sample, range, outputs, instance);
      const a = Math.abs(pred[name]?.mean ?? 0);
      if (a > m) m = a;
    }
    out[name] = m;
  }
  return out;
}

function expertRawScores(
  candidate: Record<string, number>,
  range: AxisSpec[],
  perOutput: PerOutputModel[],
  maxAbsMu: Record<string, number>,
  observed: SamplePoint[],
  outputs: OutputSpec[],
  instance: MannequinInstance,
): Record<TailorExpertName, number> {
  const pred = outputVector(perOutput, candidate, range, outputs, instance);
  const sigmas = Object.values(pred).map((p) => p.sigma);
  const valueUncertainty = sigmas.reduce((a, b) => a + b * b, 0);
  const activeAxes = range.filter((a) => a.name === 'alpha' || a.name === 'beta').slice(0, 2);
  let gradient = 0;
  let topology = 0;
  for (const axis of activeAxes.length > 0 ? activeAxes : range.slice(0, 2)) {
    const delta = Math.max(((axis.max ?? 1) - (axis.min ?? 0)) * 0.02, 1e-6);
    const plus = outputVector(perOutput, perturb(candidate, axis, delta), range, outputs, instance);
    const minus = outputVector(perOutput, perturb(candidate, axis, -delta), range, outputs, instance);
    for (const name of Object.keys(pred)) {
      const centre = pred[name]?.mean ?? 0;
      const p = plus[name]?.mean ?? centre;
      const m = minus[name]?.mean ?? centre;
      gradient += ((p - m) / (2 * delta)) ** 2;
      topology += ((p - 2 * centre + m) / (delta ** 2)) ** 2;
    }
  }
  const cl = pred.CL?.mean ?? 0.2;
  const cmy = pred.Cmy?.mean ?? pred.Cmz?.mean ?? 0;
  const sCl = pred.CL?.sigma ?? 0;
  const sCm = pred.Cmy?.sigma ?? pred.Cmz?.sigma ?? 0;
  const safeCl = Math.max(Math.abs(cl), 0.12);
  const downstream = Math.sqrt((sCm / safeCl) ** 2 + ((cmy * sCl) / safeCl ** 2) ** 2);
  const x = normalisePoint(candidate, range);
  const observedNorm = observed.map((p) => normalisePoint(p.inputs, range));
  const coverage = observedNorm.length === 0
    ? 1
    : Math.min(
        ...observedNorm.map((row) =>
          Math.sqrt(row.reduce((acc, v, idx) => acc + (v - (x[idx] ?? 0)) ** 2, 0)),
        ),
      );
  const muScale = Object.entries(pred).reduce((acc, [name, p]) => {
    const denom = Math.max(maxAbsMu[name] ?? 1, 1e-9);
    return acc + Math.abs(p.mean) / denom;
  }, 0);
  return {
    value_uncertainty: valueUncertainty,
    gradient: Math.sqrt(gradient) * (0.25 + muScale),
    topology: Math.sqrt(topology),
    downstream,
    coverage,
  };
}

function rankedTailorCandidates(
  candidates: Array<Record<string, number>>,
  range: AxisSpec[],
  perOutput: PerOutputModel[],
  maxAbsMu: Record<string, number>,
  observed: SamplePoint[],
  weights: Record<TailorExpertName, number>,
  outputs: OutputSpec[],
  instance: MannequinInstance,
): Array<{
  point: Record<string, number>;
  total: number;
  scores: TailorExpertScore[];
}> {
  const raw = candidates.map((point) => expertRawScores(point, range, perOutput, maxAbsMu, observed, outputs, instance));
  const normalised = Object.fromEntries(
    EXPERTS.map((name) => [name, normaliseScores(raw.map((row) => row[name]))]),
  ) as Record<TailorExpertName, number[]>;
  return candidates
    .map((point, idx) => {
      const scores = EXPERTS.map((name) => ({
        name,
        score: normalised[name][idx] ?? 0,
        weight: weights[name],
      }));
      const total = scores.reduce((acc, item) => acc + item.score * item.weight, 0);
      return { point, total, scores };
    })
    .sort((a, b) => b.total - a.total);
}

function leadingExpert(scores: TailorExpertScore[]): TailorExpertName {
  return scores.reduce((best, next) =>
    next.score * next.weight > best.score * best.weight ? next : best,
  ).name;
}

export function proposeTailorMannequinBatch(
  campaignId: string,
  config: CampaignConfig,
  observed: SamplePoint[],
  batchSize: number,
  iteration: number,
  instance: MannequinInstance,
  postDivergenceSampling?: PostDivergenceSamplingPolicy,
): TailorProposal[] {
  const range = rangeAxes(config.inputs);
  if (range.length === 0) return [];
  const rng = makeRng(campaignId, `tailor-${iteration}`);
  const weights = normaliseWeights(config.sampler?.tailorWeights);
  const symmetryPolicies = config.sampler?.symmetries ?? config.sampler?.symmetry;
  const samplingInputs = applySymmetrySamplingInputs(config.inputs, symmetryPolicies);
  const samplingRange = rangeAxes(samplingInputs);
  const frontier = inferDivergenceFrontier(
    config.inputs,
    config.outputs,
    observed,
    postDivergenceSampling,
  );

  if (observed.length === 0) {
    return initialDesign(samplingInputs, rng, batchSize)
      .filter((inputs) => isAllowedBySymmetrySampling(inputs, config.inputs, symmetryPolicies))
      .slice(0, batchSize)
      .map((inputs) => ({
      inputs,
      decision: {
        engine: 'tailor-mannequin',
        label: 'Tailor + Mannequin',
        selectedBy: 'initial_design',
        reason: 'Initial Latin-hypercube seed for a fresh Mannequin instance',
        mannequinInstanceId: instance.id,
        ...(frontier ? { frontierLimit: frontier.limit } : {}),
      },
    }));
  }

  let workingObserved = [...observed];
  const picks: TailorProposal[] = [];
  for (let b = 0; b < batchSize; b++) {
    const perOutput = fitTailorResidualOutputs(config, workingObserved, instance);
    const maxAbsMu = computeMaxAbsTailorMu(config.inputs, range, perOutput, rng, config.outputs, instance);
    const candidates = candidateGrid(samplingRange, rng)
      .map((point) => fillFixed(point, config.inputs))
      .filter((point) => isAllowedByDivergenceFrontier(point, frontier))
      .filter((point) => isAllowedBySymmetrySampling(point, config.inputs, symmetryPolicies));
    if (candidates.length === 0) break;
    const scoredObserved = symmetryAugmentedObservations(config.inputs, config.outputs, workingObserved, symmetryPolicies);
    const ranked = rankedTailorCandidates(
      candidates,
      range,
      perOutput,
      maxAbsMu,
      scoredObserved,
      weights,
      config.outputs,
      instance,
    );
    const best = ranked[0];
    if (!best) break;
    const selectedBy = leadingExpert(best.scores);
    picks.push({
      inputs: best.point,
      decision: {
        engine: 'tailor-mannequin',
        label: 'Tailor + Mannequin',
        selectedBy,
        reason: `Tailor blended ${selectedBy.replace('_', ' ')} with Mannequin residual uncertainty`,
        expertScores: best.scores,
        totalScore: best.total,
        mannequinInstanceId: instance.id,
        ...(frontier ? { frontierLimit: frontier.limit } : {}),
      },
    });
    const predictions = outputVector(perOutput, best.point, range, config.outputs, instance);
    workingObserved = [
      ...workingObserved,
      {
        id: `__tailor_hallucinated_${iteration}_${b}`,
        iter: iteration,
        batchId: '__stub__',
        status: 'done',
        source: 'sampler',
        inputs: best.point,
        outputs: Object.fromEntries(
          config.outputs.map((o) => [o.name, predictions[o.name]?.mean ?? 0]),
        ),
      },
    ];
  }
  return picks;
}

export function scoreTailorMannequinPoint(
  config: CampaignConfig,
  observed: SamplePoint[],
  point: Record<string, number>,
  instance = createMannequinInstance('score', config),
): number {
  const range = rangeAxes(config.inputs);
  if (range.length === 0) return 0;
  const perOutput = fitTailorResidualOutputs(config, observed, instance);
  const maxAbsMu = computeMaxAbsTailorMu(config.inputs, range, perOutput, makeRng('score', 'tailor'), config.outputs, instance);
  const weights = normaliseWeights(config.sampler?.tailorWeights);
  const ranked = rankedTailorCandidates(
    [point],
    range,
    perOutput,
    maxAbsMu,
    observed,
    weights,
    config.outputs,
    instance,
  );
  return ranked[0]?.total ?? 0;
}

export function scoreTailorMannequinCandidates(
  config: CampaignConfig,
  observed: SamplePoint[],
  candidates: Array<Record<string, number>>,
  instance = createMannequinInstance('score', config),
): number[] {
  const range = rangeAxes(config.inputs);
  if (range.length === 0 || candidates.length === 0) return candidates.map(() => 0);
  const perOutput = fitTailorResidualOutputs(config, observed, instance);
  const maxAbsMu = computeMaxAbsTailorMu(config.inputs, range, perOutput, makeRng('score', 'tailor'), config.outputs, instance);
  const weights = normaliseWeights(config.sampler?.tailorWeights);
  const raw = candidates.map((point) =>
    expertRawScores(point, range, perOutput, maxAbsMu, observed, config.outputs, instance),
  );
  const normalised = Object.fromEntries(
    EXPERTS.map((name) => [name, normaliseScores(raw.map((row) => row[name]))]),
  ) as Record<TailorExpertName, number[]>;
  return candidates.map((_, idx) =>
    EXPERTS.reduce(
      (acc, name) => acc + (normalised[name][idx] ?? 0) * weights[name],
      0,
    ),
  );
}
